// Static content for the Breaches lookup page sections. Server-safe (no client
// hooks) so section components can import it in either environment.

import type { ComponentType } from "react";
import {
  Shield,
  IconGithub,
  Key,
  LockFilled,
  ShieldFilled,
  AlertTriangleFilled,
  ZapFilled,
  FileTextFilled,
  CheckCircleFilled,
} from "@/components/icons";

export type BreachesIcon = ComponentType<{ className?: string }>;

export interface BreachesExplainer {
  icon: BreachesIcon;
  title: string;
  desc: string;
  href?: string;
  tag?: string;
  mockup?: "password" | "github" | "exposed";
}

export interface BreachesDataPoint {
  icon: BreachesIcon;
  title: string;
  desc: string;
}

export interface BreachesFaq {
  question: string;
  answer: string;
}

export interface BreachesPricingRow {
  field: string;
  details: string;
  credits: string;
}

export const BREACHES_EXPLAINERS: BreachesExplainer[] = [
  {
    icon: Shield,
    title: "Password breaches",
    desc: "Check whether a password has appeared in known data breaches, privately via k-anonymity - the password never leaves your side.",
    href: "https://docs.encrata.com/api-reference/endpoint/password-breaches",
    mockup: "password",
  },
  {
    icon: IconGithub,
    title: "GitHub leaks",
    desc: "Scan a repository's code and full git history for hardcoded secrets: API keys, tokens and private keys.",
    mockup: "github",
  },
  {
    icon: Key,
    title: "Exposed API keys",
    desc: "Discover your organization's keys already leaking in public code, gists and paste sites - matched to assets you own.",
    mockup: "exposed",
  },
];

export const BREACHES_DATA_POINTS: BreachesDataPoint[] = [
  { icon: AlertTriangleFilled, title: "Exposure status", desc: "A clear found / not-found verdict on whether the secret has leaked." },
  { icon: ZapFilled, title: "Times seen", desc: "How many times the password has appeared across known breach corpora." },
  { icon: LockFilled, title: "k-anonymity", desc: "The password is hashed in the browser, and only the first 5 characters of the hash reach the breach source." },
  { icon: FileTextFilled, title: "Breach sources", desc: "Which known breaches and leaks the secret has been associated with." },
  { icon: ShieldFilled, title: "Risk grade", desc: "A severity read on how dangerous the exposure is, so you can prioritise." },
  { icon: CheckCircleFilled, title: "Remediation", desc: "A clear recommendation to rotate or stop using the compromised secret." },
];

export const BREACHES_FAQS: BreachesFaq[] = [
  {
    question: "Does my password ever leave my device?",
    answer:
      "No. Your browser hashes the password with SHA-1 and sends only that hash to Encrata. From there we use k-anonymity: just the first 5 characters of the hash go to the breach source, so the check itself can't leak the secret.",
  },
  {
    question: "Where does the breach data come from?",
    answer:
      "From large, well-known collections of leaked credentials aggregated from public data breaches. We match against hashed records, never plaintext passwords.",
  },
  {
    question: "What does a positive result mean?",
    answer:
      "It means that exact password has appeared in at least one known breach. It does not mean your specific account was breached - but the password is unsafe to use and should be rotated.",
  },
  {
    question: "Can I check emails for breaches too?",
    answer:
      "Yes. Email breach exposure is part of the Email lookup, which reports which known breaches an address appears in and what data was exposed.",
  },
  {
    question: "Are GitHub leaks and exposed API keys available yet?",
    answer:
      "Yes, both are live. Scan a repository's code and full git history for hardcoded secrets, and sweep public code, gists and paste sites for exposed API keys scoped to assets you own, with the same credit system.",
  },
];

export const BREACHES_PRICING_ROWS: BreachesPricingRow[] = [
  { field: "Password breach", details: "Check if a password appears in breach corpora", credits: "1 credit" },
  { field: "GitHub leaks", details: "Scan the codebase and Git history for hardcoded secrets", credits: "1 credit per leak found" },
  { field: "Exposed API keys", details: "Find API keys exposed in public code and other sources", credits: "1 credit per leak found" },
];

// Example passwords offered in the live demo (all known-leaked, safe to show).
export const BREACHES_DEMO_EXAMPLES = ["password123", "Tr0ub4dor&3", "qwerty"];
